<template>
  <div class="agent-steps" :class="{ 'agent-steps--live': live }">
    <button
      v-if="!live"
      type="button"
      class="agent-steps__toggle"
      :aria-expanded="isOpen"
      @click="isOpen = !isOpen"
    >
      <Icon :icon="isOpen ? 'mdi:chevron-down' : 'mdi:chevron-right'" />
      <span>{{ steps.length }} {{ steps.length === 1 ? "step" : "steps" }}</span>
    </button>
    <ul v-if="live || isOpen" class="agent-steps__list">
      <li v-for="(step, index) in steps" :key="index" class="agent-steps__item">
        <Icon class="agent-steps__icon" :icon="iconFor(step.type)" />
        <span class="agent-steps__text">{{ step.text }}</span>
        <span v-if="step.detail" class="agent-steps__detail" :title="step.detail">
          {{ step.detail }}
        </span>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { Icon } from "@/components/ui";
import type { AgentStep } from "@/dtos/Agent.dto";

// What the agent did during a turn: listed as it happens while the turn runs
// (`live`), then folded into an "N steps" toggle above the reply.
defineProps<{
  steps: AgentStep[];
  live?: boolean;
}>();

const isOpen = ref(false);

const ICONS: Record<string, string> = {
  search: "mdi:magnify",
  read: "mdi:file-document-outline",
  analyze: "mdi:chart-box-outline",
  linkedin: "mdi:linkedin",
  enrich: "mdi:account-search-outline",
  file: "mdi:file-table-outline",
  task: "mdi:checkbox-marked-circle-outline",
};

const iconFor = (type: string): string => ICONS[type] ?? ICONS.task;
</script>

<style scoped>
.agent-steps {
  align-self: flex-start;
  max-width: 100%;
  color: #6b7280;
  font-size: 13px;
}

.agent-steps__toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px 2px 2px;
  border: none;
  border-radius: 6px;
  background: none;
  color: #6b7280;
  font-size: 13px;
  cursor: pointer;
}

.agent-steps__toggle:hover {
  background: #f3f4f6;
  color: #374151;
}

.agent-steps__list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 4px 0 4px 6px;
  list-style: none;
}

.agent-steps__item {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}

.agent-steps__icon {
  flex-shrink: 0;
  align-self: center;
  font-size: 14px;
}

.agent-steps__text {
  flex-shrink: 0;
  color: #374151;
}

.agent-steps__detail {
  overflow: hidden;
  min-width: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The newest step is the one still running. */
.agent-steps--live .agent-steps__item:last-child .agent-steps__text {
  color: #111827;
  font-weight: 500;
}
</style>
